import { IContextualMenuItem, IContextualMenuProps, IDropdownOption, MessageBarType, PivotItem } from '@fluentui/react';
import {
  Button,
  buttonStylesGhost,
  buttonStylesPrimary,
  Dropdown,
  getDescriptionForItemCount,
  IconName,
  Loader,
  loaderStylesSpinnerXLarge,
  navigate as openLink,
  PageHeader,
  pageHeaderStylesSecondary,
  Pivot,
  pivotContainerStylesFill,
  pivotStylesFill,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { AuthContextProps, useAuth } from 'react-oidc-context';
import { useHistory, useParams } from 'react-router-dom';

import { AccessList } from '../../../components/AccessList/AccessList';
import { ArtifactList } from '../../../components/ArtifactList/ArtifactList';
import { EditFeatureSetPanel } from '../../../components/EditFeatureSetPanel/EditFeatureSetPanel';
import { FeatureSetList } from '../../../components/FeatureSetList/FeatureSetList';
import { FeatureSetMetaData } from '../../../components/FeatureSetMetaData/FeatureSetMetaData';
import { FeatureSetPreview } from '../../../components/FeatureSetPreview/FeatureSetPreview';
import { IngestHistoryList } from '../../../components/IngestHistoryList/IngestHistoryList';
import { IngestPanel } from '../../../components/IngestPanel/IngestPanel';
import { JobList } from '../../../components/JobList/JobList';
import { RegisterFeatureSetPanel } from '../../../components/RegisterFeatureSetPanel/RegisterFeatureSetPanel';
import { RetrievePanel } from '../../../components/RetrievePanel/RetrievePanel';
import { ScheduledTaskList } from '../../../components/ScheduledTaskList/ScheduledTaskList';
import { SchedulePanel } from '../../../components/SchedulePanel/SchedulePanel';
import { UploadArtifactPanel } from '../../../components/UploadArtifactPanel/UploadArtifactPanel';
import { VersionList } from '../../../components/VersionList/VersionList';
import { fsApi } from '../../../featurestore/api';
import { useQueryWebConfig } from '../../../featurestore/featurestore.hooks';
import {
  ActivePermission,
  ArtifactType,
  CodeSnippedIdResponse,
  CoreService,
  IsFeatureSetPinnedResponse,
  JobStatus,
  JobType,
  ListableFeatureSet,
  PermissionType,
  ResourceType,
  RetrieveArtifactResponse,
  ScheduledTask,
  StartMaterializationOnlineRequest,
  UserBasicInfo,
  UserWithPermission,
} from '../../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { FeatureSetFlow } from '../../../featurestore/gen/ai/h2o/featurestore/api/v1/FeatureSetProtoApi.pb';
import { ContentsTitle, FeatureSetAction, jobStatusNameMap, jobTypeNameMap } from '../../../featurestore/models';
import { featurestorePageClassName } from '../../../featurestore/styles';
import {
  fetchTextReq,
  getDropdownOptionsByKeyNameMap,
  getErrorMessageBarMessage,
  hasAPIError,
  hasGreaterPermission,
} from '../../../featurestore/utils';
import { RoutePaths } from '../../../pages/Routes';
import { useSearchParams } from '../../../utils/hooks';
import {
  featureSetDetailsStylesDefault,
  IFeatureSetDetailsClassNames,
  IFeatureSetDetailsStyles,
} from './FeatureSetDetails.styles';

export interface ListableFeatureSetWithOwners extends ListableFeatureSet {
  owners?: UserBasicInfo[];
}

const contentsTitleDownloadFeatures = `${ContentsTitle.download} ${ContentsTitle.features}`;

const getMoreMenuProps = (
  data: ListableFeatureSet = {},
  codeSnippedId: string | undefined,
  onItemClick: (
    ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    item?: IContextualMenuItem
  ) => boolean | void
): IContextualMenuProps => {
  const items = [];
  const editable = hasGreaterPermission(data?.permission, ActivePermission.ACTIVE_PERMISSION_EDITOR);
  if (editable) {
    if (data?.flow === FeatureSetFlow.OFFLINE_ONLINE_MANUAL) {
      items.push({ key: ContentsTitle.onlineMaterialize, text: ContentsTitle.onlineMaterialize });
    }
    items.push({ key: ContentsTitle.uploadArtifact, text: ContentsTitle.uploadArtifact });
  }
  if (hasGreaterPermission(data?.permission, ActivePermission.ACTIVE_PERMISSION_CONSUMER)) {
    items.push({ key: ContentsTitle.codeSnippet, text: ContentsTitle.codeSnippet, disabled: !codeSnippedId });
  }
  items.push({ key: contentsTitleDownloadFeatures, text: contentsTitleDownloadFeatures });
  if (editable) {
    items.push({ key: ContentsTitle.createNewMajorVersion, text: ContentsTitle.createNewMajorVersion });
    items.push({ key: ContentsTitle.schedule, text: ContentsTitle.schedule });
  }
  return {
    items,
    onItemClick,
  };
};

const downloadFeaturesCSV = async ({ id, name }: ListableFeatureSet = {}, authService: AuthContextProps) => {
  const csv = await fetchTextReq(`/api/v1/feature-sets/${id}/features/csv`, {
    headers: { Authorization: `Bearer ${authService.user?.access_token}` },
    method: 'GET',
  });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  openLink(url, true, `${name?.replace(' ', '_')}.csv`);
};

const getListAPIs = (
  featureSetId: string | undefined,
  reviewId: string | undefined,
  title?: ContentsTitle,
  featureSetVersion?: string
) => {
  switch (title) {
    case ContentsTitle.pendingReviews:
      return [
        fsApi(CoreService.GetListableFeatureSetToReview, {
          reviewId,
        }),
      ];
    case ContentsTitle.pendingFeatureSets:
      return [
        fsApi(CoreService.GetListableFeatureSetInReview, {
          reviewId,
        }),
      ];
    default:
      return [
        fsApi(CoreService.GetListableFeatureSet, {
          featureSetId,
          featureSetVersion,
        }),
        fsApi(CoreService.GetUserFeatureSetPermissions, {
          resourceId: featureSetId,
          permissionFilter: PermissionType.Owner,
          pageSize: 20,
        }),
      ];
  }
};

interface IFeatureSetDetailsProps {
  title?: ContentsTitle;
}

export const FeatureSetDetails = ({ title }: IFeatureSetDetailsProps) => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab');
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [data, setData] = useState<ListableFeatureSetWithOwners>();
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [showNewVersionPanel, setShowNewVersionPanel] = useState(false);
  const [showUploadArtifactPanel, setShowUploadArtifactPanel] = useState(false);
  const [showIngestPanel, setShowIngestPanel] = useState(false);
  const [showRetrievePanel, setShowRetrievePanel] = useState(false);
  const [showSchedulePanel, setShowSchedulePanel] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [reload, setReload] = useState(false);
  const [codeSnippedId, setCodeSnipedId] = useState<string>();
  const [jobStatus, setJobStatus] = useState('all');
  const [jobTypeFilters, setJobTypeFilters] = useState<JobType[]>([]);
  const [description, setDescription] = useState('');
  const [selectedPivotKey, setSelectedPivotKey] = useState(tab ? tab : ContentsTitle.metaData);
  const { featureSetId, reviewId, version } = useParams<{ featureSetId: string; reviewId: string; version: string }>();
  const refVersion = useRef<string>(version || '');
  const refMeta = useRef<{
    scheduledTask?: ScheduledTask;
  }>({});
  const authService = useAuth();
  const history = useHistory();
  const { addToast } = useToast();
  const webConfigQuery = useQueryWebConfig();
  const dataDownloadEnabled = Boolean(webConfigQuery.data?.uiDataDownloadEnabled);
  const onUpdateData = useCallback((count: number, pageSize?: number, pageToken?: string, name?: string) => {
    name
      ? setDescription(getDescriptionForItemCount(count, pageSize, pageToken, name))
      : setDescription(`${count} rows. Preview displays up to 100 rows and 50 features`);
    setReload(false);
  }, []);
  const load = useCallback(async () => {
    setLoadingMessage(`Loading feature set...`);
    const results = await Promise.all(
      getListAPIs(featureSetId, reviewId, title, refVersion.current).map((p) => p.catch((e) => e))
    );
    const resultData: { listableFeatureSet: ListableFeatureSet; users: UserWithPermission[] } = {
      listableFeatureSet: {},
      users: [],
    };
    let hasError = false;
    results.forEach((d, i) => {
      if (hasAPIError(d)) {
        addToast(getErrorMessageBarMessage(d, `An error has occurred while loading Feature Set: `));
        hasError = true;
      } else {
        switch (i) {
          case 0:
            resultData.listableFeatureSet = d.listableFeatureSet;
            refVersion.current = d.listableFeatureSet.version;
            break;
          case 1:
            resultData.users = d.users;
            break;
        }
      }
    });
    setData({ ...resultData.listableFeatureSet, owners: resultData.users.map(({ user = {} }) => user) });
    setLoadingMessage('');
    // when failing loading the details, it should go to the list.
    if (hasError) {
      history.push(RoutePaths.FEATURESTORE_FEATURE_SETS);
    } else {
      if (!title) loadCodeSnippedId(resultData.listableFeatureSet);
    }
  }, [addToast, featureSetId]);
  const loadPinned = useCallback(async () => {
    setPinning(true);
    try {
      const { pinned }: IsFeatureSetPinnedResponse = await fsApi(CoreService.IsFeatureSetPinned, { featureSetId });
      setPinned(!!pinned);
    } catch (err) {
      addToast(
        getErrorMessageBarMessage(err, `An error has occurred while loading the feature set is pinned or not: `)
      );
    } finally {
      setPinning(false);
    }
  }, [addToast, featureSetId]);
  const loadCodeSnippedId = useCallback(
    async ({ id: featureSetId, version: featureSetMajorVersion }: ListableFeatureSet) => {
      try {
        const { artifactId }: CodeSnippedIdResponse = await fsApi(CoreService.GetCodeSnippedId, {
          featureSetId,
          featureSetMajorVersion: featureSetMajorVersion !== undefined ? parseInt(featureSetMajorVersion) : undefined,
        });
        setCodeSnipedId(artifactId);
      } catch (err) {
        setCodeSnipedId('');
      }
    },
    [addToast]
  );
  const loadArtifact = useCallback(
    async (artifactId?: string) => {
      try {
        const { artifact: { url, artifactType, filename } = {} }: RetrieveArtifactResponse = await fsApi(
          CoreService.RetrieveArtifact,
          { artifactId }
        );
        if (url) {
          if (artifactType === ArtifactType.ARTIFACT_TYPE_LINK) {
            openLink(url);
          } else if (artifactType === ArtifactType.ARTIFACT_TYPE_FILE) {
            openLink(url, true, filename);
          }
        }
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while downloading Snipped Code: `));
      }
    },
    [addToast]
  );
  const onPin = useCallback(
    async (pinned) => {
      setPinning(true);
      try {
        if (pinned) {
          await fsApi(CoreService.PinFeatureSet, {
            featureSetId,
          });
        } else {
          await fsApi(CoreService.UnpinFeatureSet, {
            featureSetId,
          });
        }
        setPinned(pinned);
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while pinning the feature set: `));
      } finally {
        setPinning(false);
        loadPinned();
      }
    },
    [addToast, featureSetId]
  );
  const onSync = useCallback(async () => {
    setSyncing(true);
    try {
      await fsApi(CoreService.StartOnlineOfflineIngestionJob, {
        featureSetId,
        featureSetVersion: data?.version,
      });
      addToast({
        messageBarType: MessageBarType.success,
        message: `You have successfully started to sync online to offline`,
      });
      setSelectedPivotKey(ContentsTitle.jobs);
      setReload(true);
    } catch (err) {
      addToast(
        getErrorMessageBarMessage(err, `An error has occurred while starting online to offline sync the feature set: `)
      );
    } finally {
      setSyncing(false);
    }
  }, [addToast, featureSetId, data]);
  const onOnlineMaterialize = useCallback(async () => {
    setLoadingMessage(`Loading feature set...`);
    const { owners, ...featureSet } = data || {};
    try {
      await fsApi(CoreService.StartMaterializationOnlineJob, {
        featureSet,
        featureSetId,
        featureSetVersion: featureSet.version,
      } as StartMaterializationOnlineRequest);
      await load();
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while online materializing: `));
    } finally {
      setLoadingMessage('');
    }
  }, [featureSetId]);
  const onChangeJobStatusFilters = useCallback((_e: FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    if (option) {
      setJobStatus(option.key as string);
    }
  }, []);
  const onChangeJobTypeFilters = useCallback(
    (_e: FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
      if (option) {
        const key = option.key as JobType;
        if (option.selected) {
          if (!jobTypeFilters.includes(key)) {
            setJobTypeFilters([...jobTypeFilters, key]);
          }
        } else {
          if (jobTypeFilters.includes(key)) {
            setJobTypeFilters(jobTypeFilters.filter((s) => s !== key));
          }
        }
      }
    },
    [jobTypeFilters]
  );
  const onMoreMenuItemClick = useCallback(
    (_e?: React.MouseEvent | React.KeyboardEvent, item?: IContextualMenuItem) => {
      if (item) {
        switch (item.key) {
          case ContentsTitle.onlineMaterialize:
            onOnlineMaterialize();
            break;
          case ContentsTitle.uploadArtifact:
            setShowUploadArtifactPanel(true);
            break;
          case ContentsTitle.codeSnippet:
            loadArtifact(codeSnippedId);
            break;
          case contentsTitleDownloadFeatures:
            downloadFeaturesCSV(data, authService);
            break;
          case ContentsTitle.schedule:
            setShowSchedulePanel(true);
            break;
          case ContentsTitle.createNewMajorVersion:
            setShowNewVersionPanel(true);
            break;
        }
      }
    },
    [onOnlineMaterialize, loadArtifact, authService, data, codeSnippedId]
  );
  const onEditSchedule = useCallback((task: ScheduledTask) => {
    refMeta.current.scheduledTask = task;
    setShowSchedulePanel(true);
  }, []);
  const onChangeVersion = useCallback(
    (version?: string) => {
      if (version) {
        history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${featureSetId}/version/${version}`);
        refVersion.current = version;
        setSelectedPivotKey(ContentsTitle.metaData);
        load();
      }
    },
    [featureSetId]
  );
  useEffect(() => {
    if (!title) {
      loadPinned();
    }
    load();
  }, [load]);
  const classNames = useClassNames<IFeatureSetDetailsStyles, IFeatureSetDetailsClassNames>(
    'FeatureSetDetails',
    featureSetDetailsStylesDefault,
    undefined,
    featurestorePageClassName
  );
  return loadingMessage ? (
    <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
  ) : (
    <div className={classNames.root} data-test="feature-set-details">
      <PageHeader
        styles={pageHeaderStylesSecondary}
        style={{ marginBottom: 24 }}
        pageTitle={`${data?.name} (v${refVersion.current})`}
        breadcrumbProps={{
          items: [
            {
              text: data?.projectName || '',
              key: 'Project Name',
              onClick: () =>
                history.push(`${RoutePaths.FEATURESTORE_PROJECT}/${data?.projectId}?name=${data?.projectName}`),
            },
            {
              text: data?.name || '',
              key: 'Feature set Name',
              isCurrentItem: true,
            },
          ],
        }}
        actions={
          title ? undefined : (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                styles={buttonStylesGhost}
                text={pinned ? 'Unpin' : 'Pin'}
                loading={pinning}
                iconName={pinned ? IconName.PinnedSolid : IconName.Pinned}
                onClick={() => onPin(!pinned)}
              />
              {hasGreaterPermission(data?.permission, ActivePermission.ACTIVE_PERMISSION_EDITOR) && (
                <>
                  <Button
                    styles={buttonStylesGhost}
                    text="Edit"
                    iconName="Edit"
                    onClick={() => setShowEditPanel(true)}
                  />
                  <Button styles={buttonStylesGhost} text="Online to Offline Sync" loading={syncing} onClick={onSync} />
                  <Button
                    styles={buttonStylesGhost}
                    text={ContentsTitle.ingest}
                    onClick={() => setShowIngestPanel(true)}
                  />
                </>
              )}
              {dataDownloadEnabled &&
                hasGreaterPermission(data?.permission, ActivePermission.ACTIVE_PERMISSION_CONSUMER) && (
                  <Button
                    styles={buttonStylesGhost}
                    text={ContentsTitle.retrieve}
                    onClick={() => setShowRetrievePanel(true)}
                  />
                )}
              <Button
                styles={buttonStylesPrimary}
                text="More"
                menuProps={getMoreMenuProps(data, codeSnippedId, onMoreMenuItemClick)}
              />
            </div>
          )
        }
      />
      <Pivot
        styles={pivotStylesFill}
        pivotContainerStyles={pivotContainerStylesFill}
        selectedKey={selectedPivotKey}
        data-test="metadata-preview-pivot"
        onLinkClick={(item?: PivotItem) => {
          const title = item?.props.itemKey as ContentsTitle;
          if (title) {
            setSelectedPivotKey(title);
            params.set('tab', title);
            setParams(params);
          }
        }}
        items={[
          {
            key: ContentsTitle.metaData,
            headerText: ContentsTitle.metaData,
            content: (
              <FeatureSetMetaData
                title={title}
                reviewId={reviewId}
                data={data || {}}
                onOpenFeaturePage={(data) =>
                  history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${featureSetId}/${data.id}`)
                }
              />
            ),
          },
          {
            key: ContentsTitle.preview,
            headerText: ContentsTitle.preview,
            content: (
              <div className={classNames.list}>
                <div className={classNames.description}>{description}</div>
                <FeatureSetPreview title={title} reviewId={reviewId} featureSet={data} onUpdateData={onUpdateData} />
              </div>
            ),
          },
          ...(title
            ? []
            : [
                {
                  key: ContentsTitle.jobs,
                  headerText: ContentsTitle.jobs,
                  content: (
                    <div className={classNames.list}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className={classNames.description}>{description}</div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Dropdown
                            style={{ width: '300px' }}
                            placeholder="Filter by job types"
                            data-test="dropdown-job-type-filter"
                            multiSelect
                            selectedKeys={jobTypeFilters}
                            options={getDropdownOptionsByKeyNameMap(jobTypeNameMap)}
                            onChange={onChangeJobTypeFilters}
                          />
                          <Dropdown
                            style={{ width: '160px' }}
                            placeholder="Filter by status"
                            data-test="dropdown-job-list-filter"
                            selectedKey={jobStatus}
                            options={getDropdownOptionsByKeyNameMap(jobStatusNameMap)}
                            onChange={onChangeJobStatusFilters}
                          />
                        </div>
                      </div>
                      <JobList
                        featureSetId={featureSetId}
                        jobStatus={jobStatus === 'all' ? undefined : (jobStatus as JobStatus)}
                        jobTypeFilters={jobTypeFilters}
                        onUpdateData={onUpdateData}
                      />
                    </div>
                  ),
                },
                ...(hasGreaterPermission(data?.permission, ActivePermission.ACTIVE_PERMISSION_CONSUMER)
                  ? [
                      {
                        key: ContentsTitle.artifacts,
                        headerText: ContentsTitle.artifacts,
                        content: (
                          <div className={classNames.list}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <div className={classNames.description}>{description}</div>
                            </div>
                            <ArtifactList
                              canDelete={hasGreaterPermission(
                                data?.permission,
                                ActivePermission.ACTIVE_PERMISSION_EDITOR
                              )}
                              reload={reload}
                              featureSetId={featureSetId}
                              featureSetMajorVersion={data?.version}
                              onUpdateData={onUpdateData}
                            />
                          </div>
                        ),
                      },
                    ]
                  : []),
                {
                  key: ContentsTitle.ingestHistory,
                  headerText: ContentsTitle.ingestHistory,
                  content: (
                    <div className={classNames.list}>
                      <IngestHistoryList
                        reload={reload}
                        featureSetId={featureSetId}
                        featureSetVersion={data?.version}
                        onStartRevert={() => {
                          setSelectedPivotKey(ContentsTitle.jobs);
                          setJobTypeFilters([JobType.Revert]);
                          setReload(true);
                        }}
                      />
                    </div>
                  ),
                },
                {
                  key: ContentsTitle.versions,
                  headerText: ContentsTitle.versions,
                  content: (
                    <div className={classNames.list}>
                      <VersionList
                        isOwner={data?.permission === ActivePermission.ACTIVE_PERMISSION_OWNER}
                        reload={reload}
                        featureSetId={featureSetId}
                        onView={(item) => onChangeVersion(item.version)}
                        onDelete={() => {
                          history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${featureSetId}`);
                          refVersion.current = '';
                          load();
                        }}
                      />
                    </div>
                  ),
                },
                {
                  key: ContentsTitle.scheduledTasks,
                  headerText: ContentsTitle.scheduledTasks,
                  content: (
                    <div className={classNames.list}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div className={classNames.description}>{description}</div>
                      </div>
                      <ScheduledTaskList
                        canEdit={hasGreaterPermission(data?.permission, ActivePermission.ACTIVE_PERMISSION_EDITOR)}
                        reload={reload}
                        featureSetId={featureSetId}
                        onUpdateData={onUpdateData}
                        onEdit={onEditSchedule}
                      />
                    </div>
                  ),
                },
              ]),
          {
            key: ContentsTitle.accessList,
            headerText:
              data?.permission === ActivePermission.ACTIVE_PERMISSION_OWNER
                ? ContentsTitle.accessList
                : ContentsTitle.myAccess,
            content: (
              <div className={classNames.list}>
                <AccessList
                  resourceType={ResourceType.FEATURE_SET}
                  id={data?.id}
                  name={data?.name}
                  activePermission={data?.permission}
                />
              </div>
            ),
          },

          {
            key: ContentsTitle.derivedFeatureSets,
            headerText: ContentsTitle.derivedFeatureSets,
            content: (
              <div className={classNames.list}>
                <FeatureSetList
                  childVersionId={
                    data?.id && data?.version ? { id: data.id, majorVersion: parseInt(data.version) } : {}
                  }
                  onView={(item) => {
                    history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${item.id}`);
                    setSelectedPivotKey(ContentsTitle.metaData);
                    load();
                  }}
                />
              </div>
            ),
          },
          {
            key: ContentsTitle.parentFeatureSets,
            headerText: ContentsTitle.parentFeatureSets,
            content: (
              <div className={classNames.list}>
                <FeatureSetList
                  parentVersionIds={data?.derivedFrom?.featureSetIds || []}
                  onView={(item) => {
                    history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${item.id}`);
                    setSelectedPivotKey(ContentsTitle.metaData);
                    refVersion.current = '';
                    load();
                  }}
                />
              </div>
            ),
          },
        ]}
      />
      <EditFeatureSetPanel
        id={data?.id}
        name={data?.name}
        permission={data?.permission}
        show={showEditPanel}
        onDismiss={(_e, updatedFeatureSet, deleted) => {
          if (deleted) {
            history.push(RoutePaths.FEATURESTORE_FEATURE_SETS);
          } else if (updatedFeatureSet) {
            onChangeVersion(updatedFeatureSet.version);
          }
          setShowEditPanel(false);
        }}
      />
      <UploadArtifactPanel
        featureSetId={data?.id}
        featureSetName={data?.name}
        featureSetMajorVersion={data?.version}
        show={showUploadArtifactPanel}
        onDismiss={(_e, uploaded) => {
          if (uploaded) {
            setSelectedPivotKey(ContentsTitle.artifacts);
            setReload(true);
          }
          setShowUploadArtifactPanel(false);
        }}
      />
      <IngestPanel
        featureSetId={data?.id}
        featureSetName={data?.name}
        featureSetVersion={data?.version}
        show={showIngestPanel}
        onDismiss={(_e, uploaded, isOnline) => {
          if (uploaded) {
            if (isOnline) {
              setSelectedPivotKey(ContentsTitle.metaData);
              load();
            } else {
              setSelectedPivotKey(ContentsTitle.jobs);
              setReload(true);
            }
          }
          setShowIngestPanel(false);
        }}
      />
      <RetrievePanel
        featureSet={data}
        show={showRetrievePanel}
        onDismiss={(_e, uploaded, isOnline) => {
          if (uploaded) {
            if (isOnline) {
              setSelectedPivotKey(ContentsTitle.metaData);
              load();
            } else {
              setSelectedPivotKey(ContentsTitle.jobs);
              setJobTypeFilters([JobType.Retrieve]);
              setReload(true);
            }
          }
          setShowRetrievePanel(false);
        }}
      />
      <SchedulePanel
        featureSetId={data?.id}
        featureSetName={data?.name}
        featureSetVersion={data?.version}
        projectId={data?.projectId}
        task={refMeta.current.scheduledTask}
        show={showSchedulePanel}
        onDismiss={(_e, newTask) => {
          refMeta.current.scheduledTask = undefined;
          if (newTask) {
            setSelectedPivotKey(ContentsTitle.scheduledTasks);
            setReload(true);
          }
          setShowSchedulePanel(false);
        }}
      />
      <RegisterFeatureSetPanel
        action={FeatureSetAction.newVersion}
        featureSetId={data?.id}
        show={showNewVersionPanel}
        onDismiss={(updated, updateInfo) => {
          setShowNewVersionPanel(false);
          const { version, extractSchema, backfill } = updateInfo || {};
          if (updated) {
            if (extractSchema) {
              history.push(`${RoutePaths.FEATURESTORE_FEATURE_SETS}?tab=${ContentsTitle.ongoingRegistration}`);
            } else {
              if (backfill) {
                setSelectedPivotKey(ContentsTitle.jobs);
                setJobTypeFilters([JobType.Backfill]);
                setReload(true);
              } else {
                onChangeVersion(version);
              }
            }
          }
        }}
      />
    </div>
  );
};
